/**
 * Zod schemas for the auth screens (STORY-001-009).
 *
 * The error messages here are i18n KEYS, not human strings — the form
 * UI passes the key through `useTranslation('auth')` before rendering.
 * This keeps the schema purely about shape + a stable key set, and lets
 * the i18n CI coverage check pick up the keys via static analysis.
 *
 * Pinned constants:
 *   - Minimum password length: 12 (REQ-PRD-§11.1-C / Implementation
 *     logic step 2).
 *   - OTP: exactly 6 digits (matches the backend's
 *     `pattern=r"^\\d{6}$"` in `garden.auth.schemas`).
 *   - Phone: E.164 `^\\+\\d{8,15}$` (matches `_E164_PATTERN` server-side).
 *   - Language enum: RU / KY / EN (per ADR-028 amendment to this story).
 *
 * Note (story body §32, "Risks & gotchas"):
 *   The backend's `SignupRequest.language` is currently `Literal['ru',
 *   'ky']`. The amended frontend schema accepts EN per ADR-028. Once
 *   STORY-012-012 or the backend amendment lands, the server-side
 *   `LanguageCode` will accept 'en' too. Until then, a signup with
 *   `language: 'en'` would 422 — the screen handles that as a
 *   localized error via the existing code-mapping flow. This drift is
 *   tracked in ADR-028's "Implementation work it triggers" section.
 */
import { z } from "zod";

export const MIN_PASSWORD_LENGTH = 12;
/** OTP length matches the backend `^\d{6}$` regex exactly. */
export const OTP_LENGTH = 6;
const OTP_REGEX = /^\d{6}$/;
const E164_REGEX = /^\+\d{8,15}$/;

/** UI language values accepted on the Signup form (per ADR-028). */
export const UI_LANGUAGES = ["ru", "ky", "en"] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];

/**
 * Shared password rule. The schema enforces the 12-char minimum; the
 * server runs the HIBP + Argon2id checks. We don't enforce extra
 * client-side rules (uppercase/symbols) because the server policy is
 * the source of truth.
 */
const passwordSchema = z
  .string({ required_error: "common.required" })
  .min(MIN_PASSWORD_LENGTH, { message: "common.password.tooShort" });

const emailSchema = z
  .string({ required_error: "common.required" })
  .min(1, { message: "common.required" })
  // The server uses RFC 5322 via pydantic-email; we use Zod's email
  // regex, which is a sane MVP filter. The server-side validator is
  // authoritative — a 422 from there surfaces as
  // `auth.signup.invalid_input` and renders as the generic validation
  // error.
  .email({ message: "common.email.invalid" });

const otpSchema = z
  .string({ required_error: "common.required" })
  .regex(OTP_REGEX, { message: "common.otp.format" });

const phoneE164Schema = z
  .string({ required_error: "common.required" })
  .regex(E164_REGEX, { message: "common.phone.format" });

// ---- SignupSchema ---------------------------------------------------

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  language: z.enum(UI_LANGUAGES),
});
export type SignupFormValues = z.infer<typeof signupSchema>;

export const signupDefaultValues: SignupFormValues = {
  email: "",
  password: "",
  language: "ru",
};

// ---- LoginSchema ----------------------------------------------------

export const loginSchema = z.object({
  email: emailSchema,
  password: z
    .string({ required_error: "common.required" })
    .min(1, { message: "common.required" }),
});
export type LoginFormValues = z.infer<typeof loginSchema>;

export const loginDefaultValues: LoginFormValues = {
  email: "",
  password: "",
};

// ---- VerifyEmailSchema ----------------------------------------------

export const verifyEmailSchema = z.object({
  otp: otpSchema,
});
export type VerifyEmailFormValues = z.infer<typeof verifyEmailSchema>;

export const verifyEmailDefaultValues: VerifyEmailFormValues = {
  otp: "",
};

// ---- ForgotPasswordSchema ------------------------------------------

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export const forgotPasswordDefaultValues: ForgotPasswordFormValues = {
  email: "",
};

// ---- ResetPasswordSchema -------------------------------------------

export const resetPasswordSchema = z.object({
  email: emailSchema,
  otp: otpSchema,
  newPassword: passwordSchema,
});
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export const resetPasswordDefaultValues: ResetPasswordFormValues = {
  email: "",
  otp: "",
  newPassword: "",
};

// ---- Phone-verify schemas (two-step) -------------------------------

export const phoneStartSchema = z.object({
  phone: phoneE164Schema,
});
export type PhoneStartFormValues = z.infer<typeof phoneStartSchema>;

export const phoneVerifySchema = z.object({
  otp: otpSchema,
});
export type PhoneVerifyFormValues = z.infer<typeof phoneVerifySchema>;

export const phoneStartDefaultValues: PhoneStartFormValues = { phone: "" };
export const phoneVerifyDefaultValues: PhoneVerifyFormValues = { otp: "" };

// ---- Helper: map ApiError.code → auth.errors.* key -----------------

/**
 * Map a server-side problem-details `code` to the auth-namespace i18n
 * key. The frontend's `errors.*` block in `locales/{ru,ky,en}/auth.json`
 * mirrors these codes verbatim so the mapping is a passthrough.
 *
 * Unknown codes fall back to `errors.generic` so a server change that
 * adds a new code surfaces gracefully (a localized "Something went
 * wrong" rather than the raw `code` string).
 */
export function authErrorKey(code: string | undefined): string {
  if (code === undefined) return "errors.generic";
  return "errors." + code;
}
